import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { NotFoundPage } from '@/components/not-found';
import { LoginPage } from '@/features/auth/LoginPage';
import { AccessRestrictedPage } from '@/features/auth/AccessRestrictedPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { OwnerDashboardPage } from '@/features/dashboard/OwnerDashboardPage';
import { AdminDashboardPage } from '@/features/dashboard/AdminDashboardPage';
import { OperationsDashboardPage } from '@/features/dashboard/OperationsDashboardPage';
import { OperationsWorkQueuePage } from '@/features/operations/OperationsWorkQueuePage';
import { OpsReviewWorkspacePage } from '@/features/operations/OpsReviewWorkspacePage';
import { RequestThreadPage } from '@/features/request-thread/RequestThreadPage';
import { EngineerAssignmentCenterPage } from '@/features/operations/EngineerAssignmentCenterPage';
import { EquipmentIntakeWorkspacePage } from '@/features/operations/EquipmentIntakeWorkspacePage';
import { ReceivingLotsPage } from '@/features/operations/ReceivingLotsPage';
import { ReceivingLotDetailPage } from '@/features/operations/ReceivingLotDetailPage';
import { ModelBatchEquipmentPage } from '@/features/operations/ModelBatchEquipmentPage';
import { BulkImportNewPage } from '@/features/operations/bulk-import/BulkImportNewPage';
import { BulkImportWorkspacePage } from '@/features/operations/bulk-import/BulkImportWorkspacePage';
import { AuthorizationCenterPage } from '@/features/authorization/AuthorizationCenterPage';
import { AuthorizationCaseDetailPage } from '@/features/authorization/AuthorizationCaseDetailPage';
import { CommercialDashboardPage } from '@/features/dashboard/CommercialDashboardPage';
import { EngineerDashboardPage } from '@/features/dashboard/EngineerDashboardPage';
import { MasterEngineerDashboardPage } from '@/features/dashboard/MasterEngineerDashboardPage';
import { RequestCenterPage } from '@/features/dashboard/RequestCenterPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { CustomerDetailPage } from '@/features/customers/CustomerDetailPage';
import { CustomerRecoveryBinPage } from '@/features/customers/CustomerRecoveryBinPage';
import { ManufacturersPage } from '@/features/master-data/ManufacturersPage';
import { EquipmentCategoriesPage } from '@/features/master-data/EquipmentCategoriesPage';
import { EquipmentTypesPage } from '@/features/master-data/EquipmentTypesPage';
import { EquipmentModelsPage } from '@/features/master-data/EquipmentModelsPage';
import { AccessoriesPage } from '@/features/master-data/AccessoriesPage';
import { ServiceTasksPage } from '@/features/master-data/ServiceTasksPage';
import { InspectionParametersPage } from '@/features/master-data/InspectionParametersPage';
import { ServiceTypesPage } from '@/features/master-data/ServiceTypesPage';
import { WarrantyTypesPage } from '@/features/master-data/WarrantyTypesPage';
import { PrioritiesPage } from '@/features/master-data/PrioritiesPage';
import { ApprovalRequestTypesPage } from '@/features/master-data/ApprovalRequestTypesPage';
import { EquipmentPage } from '@/features/equipment/EquipmentPage';
import { EquipmentDetailPage } from '@/features/equipment/EquipmentDetailPage';
import { ScanPage } from '@/features/equipment/ScanPage';
import { CompanyProfilePage } from '@/features/settings/CompanyProfilePage';
import { ReportStoragePage } from '@/features/settings/ReportStoragePage';
import { CommunicationPage } from '@/features/settings/CommunicationPage';
import { EmailDeliveryLogPage } from '@/features/settings/EmailDeliveryLogPage';
import { EmailGatewayPage } from '@/features/settings/EmailGatewayPage';
import { CommunicationTemplatesPage } from '@/features/settings/CommunicationTemplatesPage';
import { SettingsLayout } from '@/layouts/SettingsLayout';
import { EquipmentIntakePage } from '@/features/equipment-intake/EquipmentIntakePage';
import { DepartmentsPage } from '@/features/organization/DepartmentsPage';
import { RolesPage } from '@/features/organization/RolesPage';
import { UsersPage } from '@/features/organization/UsersPage';
import { UserDetailPage } from '@/features/organization/UserDetailPage';
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage';
import { PermissionRoute } from '@/components/PermissionRoute';
import { InspectionListPage } from '@/features/inspection/InspectionListPage';
import { InspectionDetailPage } from '@/features/inspection/InspectionDetailPage';
import { MyInspectionsPage } from '@/features/inspection/MyInspectionsPage';
import { JobPoolPage } from '@/features/inspection/JobPoolPage';
import { TeamQueuePage } from '@/features/inspection/TeamQueuePage';
import {
  ProcedureLibraryPage,
  ProcedureBuilderPage,
  ProcedureViewPage,
  ProcedureVersionHistoryPage,
} from '@/features/procedure';
import {
  ReportTemplateLibraryPage,
  ReportTemplateDesignerPage,
  ReportTemplateVersionsPage,
} from '@/features/report-template';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { IncompleteIdentityPage } from '@/features/organization/IncompleteIdentityPage';
import { CompanyOwnerActivationPage } from '@/features/auth/CompanyOwnerActivationPage';
import { ROUTES } from './routes';

export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <AuthLayout />,
    children: [{ index: true, element: <LoginPage /> }],
  },
  {
    path: ROUTES.CHANGE_PASSWORD,
    element: <ChangePasswordPage />,
  },
  {
    // Top-level and OUTSIDE both AppLayout and ProtectedRoute, on purpose:
    //
    //  - outside AppLayout, so the sidebar/header chrome of a workspace the user
    //    cannot use is not rendered around the message, and so
    //    <AccessRestrictionRedirect> unmounts when it navigates here (that is
    //    what makes the hop terminal rather than a cycle);
    //  - outside ProtectedRoute, so the screen still renders after the session
    //    has been cleared instead of bouncing to /login.
    //
    // It exposes nothing: the page renders four fixed strings and makes no API
    // call. Authorization is still decided entirely by the backend guards.
    path: ROUTES.ACCESS_RESTRICTED,
    element: <AccessRestrictedPage />,
  },
  {
    // Public and OUTSIDE ProtectedRoute — an invited owner has no session to
    // protect. Deliberately not inside AuthLayout either: the page renders its
    // own shell, like the customer activation page it mirrors.
    path: ROUTES.COMPANY_OWNER_ACTIVATION,
    element: <CompanyOwnerActivationPage />,
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: ROUTES.DASHBOARD_OWNER, element: <OwnerDashboardPage /> },
          { path: ROUTES.DASHBOARD_ADMIN, element: <AdminDashboardPage /> },
          { path: ROUTES.DASHBOARD_OPERATIONS, element: <OperationsDashboardPage /> },
          { path: ROUTES.DASHBOARD_COMMERCIAL, element: <CommercialDashboardPage /> },
          { path: ROUTES.DASHBOARD_ENGINEER, element: <EngineerDashboardPage /> },
          { path: ROUTES.DASHBOARD_MASTER_ENGINEER, element: <MasterEngineerDashboardPage /> },
          { path: ROUTES.REQUEST_CENTER, element: <RequestCenterPage /> },
          // Audit F-1 — customer records are tenant business data; `customer:view`
          // is the permission the backend's own list/detail endpoints require.
          {
            element: <PermissionRoute permission="customer:view" onDenied="restrict" />,
            children: [
              { path: ROUTES.CUSTOMERS, element: <CustomersPage /> },
              { path: ROUTES.CUSTOMER_DETAIL, element: <CustomerDetailPage /> },
            ],
          },
          // The Recovery Bin is NOT `customer:view`. Its API is
          // `GET /customers/deleted`, guarded by `customer:delete` — seeing what
          // was deleted is part of the delete capability, not of ordinary
          // reading. Mirrored exactly rather than assumed.
          {
            element: <PermissionRoute permission="customer:delete" onDenied="restrict" />,
            children: [{ path: ROUTES.CUSTOMERS_RECOVERY, element: <CustomerRecoveryBinPage /> }],
          },
          { path: ROUTES.EQUIPMENT, element: <EquipmentPage /> },
          { path: ROUTES.EQUIPMENT_DETAIL, element: <EquipmentDetailPage /> },
          { path: ROUTES.EQUIPMENT_SCAN, element: <ScanPage /> },
          { path: ROUTES.MANUFACTURERS, element: <ManufacturersPage /> },
          { path: ROUTES.EQUIPMENT_CATEGORIES, element: <EquipmentCategoriesPage /> },
          { path: ROUTES.EQUIPMENT_TYPES, element: <EquipmentTypesPage /> },
          { path: ROUTES.EQUIPMENT_MODELS, element: <EquipmentModelsPage /> },
          { path: ROUTES.ACCESSORIES, element: <AccessoriesPage /> },
          { path: ROUTES.SERVICE_TASKS, element: <ServiceTasksPage /> },
          { path: ROUTES.INSPECTION_PARAMETERS, element: <InspectionParametersPage /> },
          { path: ROUTES.SERVICE_TYPES, element: <ServiceTypesPage /> },
          { path: ROUTES.WARRANTY_TYPES, element: <WarrantyTypesPage /> },
          { path: ROUTES.PRIORITIES, element: <PrioritiesPage /> },
          { path: ROUTES.APPROVAL_REQUEST_TYPES, element: <ApprovalRequestTypesPage /> },
          // Audit F-1 — the Settings area. Guarded on the permission each child's
          // backend actually requires, never on a role. `company-profile:view`
          // is held by Admin, Owner, Operations, Commercial and MSE, so those
          // roles keep the access the permission model already grants them;
          // Service Engineer, which holds none of it, is refused. Report Storage
          // sits behind its own, narrower permission (see below).
          {
            element: <PermissionRoute permission="company-profile:view" onDenied="restrict" />,
            children: [
              {
                path: ROUTES.SETTINGS,
                element: <SettingsLayout />,
                children: [
                  { path: ROUTES.SETTINGS_COMPANY_PROFILE, element: <CompanyProfilePage /> },
                  {
                    // Narrower than its siblings: the backend requires
                    // `inspection:report-storage:view`, held by Admin and Owner only.
                    element: (
                      <PermissionRoute
                        permission="inspection:report-storage:view"
                        onDenied="restrict"
                      />
                    ),
                    children: [
                      { path: ROUTES.SETTINGS_REPORT_STORAGE, element: <ReportStoragePage /> },
                    ],
                  },
                  { path: ROUTES.SETTINGS_COMMUNICATION, element: <CommunicationPage /> },
                  { path: ROUTES.SETTINGS_COMMUNICATION_EMAIL, element: <EmailGatewayPage /> },
                  {
                    path: ROUTES.SETTINGS_COMMUNICATION_EMAIL_LOGS,
                    element: <EmailDeliveryLogPage />,
                  },
                  {
                    path: ROUTES.SETTINGS_COMMUNICATION_TEMPLATES,
                    element: <CommunicationTemplatesPage />,
                  },
                ],
              },
            ],
          },
          { path: ROUTES.EQUIPMENT_INTAKE, element: <EquipmentIntakePage /> },
          {
            element: (
              // Fallback is ROOT, not EQUIPMENT_INTAKE: per D-076 the engineer
              // roles hold no intake permission at all, so the intake list would
              // be a second dead end. ROOT renders DashboardPage, which
              // dispatches on roleSlug (SE -> engineer, MSE -> master-engineer).
              // Every dashboard route is permission-unguarded, so this
              // terminates in one hop and cannot loop.
              <PermissionRoute permission="intake:create" fallback={ROUTES.ROOT} />
            ),
            children: [
              { path: ROUTES.OPS_INTAKE_WORKSPACE, element: <EquipmentIntakeWorkspacePage /> },
            ],
          },
          {
            element: (
              <PermissionRoute
                permission="receiving-lot:view"
                fallback={ROUTES.DASHBOARD_OPERATIONS}
              />
            ),
            children: [
              { path: ROUTES.OPS_RECEIVING_LOTS, element: <ReceivingLotsPage /> },
              { path: ROUTES.OPS_RECEIVING_LOT_DETAIL, element: <ReceivingLotDetailPage /> },
              { path: ROUTES.OPS_RECEIVING_LOT_MODEL_BATCH, element: <ModelBatchEquipmentPage /> },
            ],
          },
          {
            element: (
              <PermissionRoute permission="bulk-import:view" fallback={ROUTES.EQUIPMENT_INTAKE} />
            ),
            children: [
              { path: ROUTES.OPS_BULK_IMPORT_NEW, element: <BulkImportNewPage /> },
              { path: ROUTES.OPS_BULK_IMPORT_DETAIL, element: <BulkImportWorkspacePage /> },
            ],
          },
          {
            element: (
              <PermissionRoute permission="inspection:all:view" fallback={ROUTES.MY_INSPECTIONS} />
            ),
            children: [{ path: ROUTES.INSPECTIONS, element: <InspectionListPage /> }],
          },
          // Request Thread Stage C — the canonical staff thread workspace.
          //
          // Gated on the SAME permission the backend route requires
          // (`inspection:modification:read`), so the two can never disagree about
          // who may open a thread. `restrict` rather than `redirect`: there is no
          // lesser version of a thread to fall back to, and bouncing someone who
          // pasted a thread URL onto a dashboard reads as a misfire rather than a
          // refusal.
          //
          // This is a courtesy, not the boundary. `PermissionGuard` answers 403
          // on GET /modification-rounds/:roundId/thread whatever renders here.
          {
            element: (
              <PermissionRoute permission="inspection:modification:read" onDenied="restrict" />
            ),
            children: [{ path: ROUTES.REQUEST_THREAD, element: <RequestThreadPage /> }],
          },
          {
            element: (
              <PermissionRoute
                permission="inspection:operations-review"
                fallback={ROUTES.DASHBOARD_OPERATIONS}
              />
            ),
            children: [
              { path: ROUTES.OPS_WORK_QUEUE, element: <OperationsWorkQueuePage /> },
              { path: ROUTES.OPS_REVIEW_WORKSPACE, element: <OpsReviewWorkspacePage /> },
            ],
          },
          {
            element: (
              <PermissionRoute
                permission="inspection:ownership:force-transfer"
                fallback={ROUTES.DASHBOARD_OPERATIONS}
              />
            ),
            children: [
              { path: ROUTES.OPS_ASSIGNMENT_CENTER, element: <EngineerAssignmentCenterPage /> },
            ],
          },
          {
            element: <PermissionRoute permission="inspection:authorization:read" />,
            children: [
              { path: ROUTES.AUTHORIZATION_CENTER, element: <AuthorizationCenterPage /> },
              { path: ROUTES.AUTHORIZATION_CASE_DETAIL, element: <AuthorizationCaseDetailPage /> },
            ],
          },
          { path: ROUTES.INSPECTION_DETAIL, element: <InspectionDetailPage /> },
          { path: ROUTES.MY_INSPECTIONS, element: <MyInspectionsPage /> },
          // B2-03 — Job Pool and Team Queue were the only inspection routes with
          // no permission gate, so a role holding neither permission (Commercial,
          // for one) could reach them by URL, get a 403 from the API and be shown
          // "No inspections in the job pool" — the D-06 mistake of dressing an
          // authorization failure as an empty result. Both now redirect the way
          // the All Inspections route already does. My Inspections stays ungated
          // on purpose: it is the fallback target, and it shows the caller only
          // their own work.
          {
            element: (
              <PermissionRoute
                permission="inspection:jobpool:view"
                fallback={ROUTES.MY_INSPECTIONS}
              />
            ),
            children: [{ path: ROUTES.JOB_POOL, element: <JobPoolPage /> }],
          },
          {
            element: (
              <PermissionRoute permission="inspection:unclaim" fallback={ROUTES.MY_INSPECTIONS} />
            ),
            children: [{ path: ROUTES.TEAM_QUEUE, element: <TeamQueuePage /> }],
          },
          // Audit F-1 — Organization. Each group carries the permission its own
          // backend controller requires, so the frontend can never be stricter
          // or looser than the API. MSE holds `user:view` in the live model and
          // therefore keeps the user list; it holds neither `department:view`
          // nor `role:view` and is refused those.
          {
            element: <PermissionRoute permission="user:view" onDenied="restrict" />,
            children: [
              { path: ROUTES.ORG_USERS, element: <UsersPage /> },
              { path: ROUTES.ORG_USER_DETAIL, element: <UserDetailPage /> },
              { path: ROUTES.ORG_INCOMPLETE_IDENTITY, element: <IncompleteIdentityPage /> },
            ],
          },
          {
            element: <PermissionRoute permission="department:view" onDenied="restrict" />,
            children: [{ path: ROUTES.ORG_DEPARTMENTS, element: <DepartmentsPage /> }],
          },
          {
            element: <PermissionRoute permission="role:view" onDenied="restrict" />,
            children: [{ path: ROUTES.ORG_ROLES, element: <RolesPage /> }],
          },
          { path: ROUTES.PROFILE, element: <ProfilePage /> },
          {
            element: <PermissionRoute permission="procedure:read" />,
            children: [
              { path: ROUTES.PROCEDURES, element: <ProcedureLibraryPage /> },
              { path: ROUTES.PROCEDURE_VERSIONS, element: <ProcedureVersionHistoryPage /> },
              { path: ROUTES.PROCEDURE_VIEW, element: <ProcedureViewPage /> },
            ],
          },
          {
            element: <PermissionRoute permission="procedure:create" />,
            children: [{ path: ROUTES.PROCEDURE_NEW, element: <ProcedureBuilderPage /> }],
          },
          {
            element: <PermissionRoute permission="procedure:update" />,
            children: [{ path: ROUTES.PROCEDURE_EDIT, element: <ProcedureBuilderPage /> }],
          },
          {
            // Report Designer (B6.9e). All three screens sit behind
            // `report-template:read` — the same permission the menu entry is
            // gated on, so a hidden menu and a blocked route can never disagree.
            //
            // Read is the correct gate for the DESIGNER route too, not `update`:
            // the page renders a PUBLISHED or ARCHIVED version read-only, which
            // a holder of `read` alone is entitled to see. The editing controls
            // inside it are gated separately on `report-template:update`, and
            // the backend refuses the write regardless of what the UI renders.
            element: <PermissionRoute permission="report-template:read" />,
            children: [
              { path: ROUTES.REPORT_TEMPLATES, element: <ReportTemplateLibraryPage /> },
              { path: ROUTES.REPORT_TEMPLATE_DESIGNER, element: <ReportTemplateDesignerPage /> },
              { path: ROUTES.REPORT_TEMPLATE_VERSIONS, element: <ReportTemplateVersionsPage /> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <div className="flex h-screen items-center justify-center bg-background">
        <NotFoundPage />
      </div>
    ),
  },
]);
